/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import { Section, Header } from 'components';
import { useParams } from 'react-router-dom';
import Content from 'containers/common/Content';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ToastMessage, AlarmSettings } from 'containers';
import { LocationRolesList } from './LocationRolesList';
import { ListView } from 'model/state';
import { Location, User, PermissionsEnum, RoleType, getLocationPermissions, LocationFilters } from 'model';
import { LocationRolesActions } from './LocationRolesActions';
import ConfirmationDialog from 'containers/common/ConfirmationDialog';
import { LocationRolesHead } from './LocationRolesHead';
import styles from './LocationRolesView.module.css';
import { useModalState } from 'effects';
import { useLocationRoleService } from './effect/useLocationRoleService';

type LocationRolesViewProps = {
  fetchUserLocationsRoles: (authUserId: string, currentUserId: string, parentId: string | undefined, page: number, search: string, filters: LocationFilters) => void,
  locationList:     ListView<Location>,
  isSelectedAll: boolean,
  setSelectAll: (selectAll: boolean) => void,
  setSelectRow: (selectAll: boolean, locationId: string) => void,
  clearMessage: () => void,
  isSelectedAny: boolean,
  selectedNumber: number,
  hasError: boolean,
  isSubmitting: boolean,
  openConfirmationDialog: (metadata?: { [key: string]: any }) => void,
  updateSearch: (search: string) => void,
  search: string,
  authUserId?: string | null,
  currentUser: User,
  isGrantFullAccessEnabled: boolean,
  isGrantLimitedAccessEnabled: boolean,
  isRevokeAccessEnabled: boolean,
  assignUserLocationRoles: (roles: PermissionsEnum[], userId: string) => void,
  parent: Location | null,
  selectedLocations: Location[],
  isSubmittingAlertSettings: boolean,
  incrementPage: () => void,
  reinitPageCount: () => void,
}

export const LocationRolesView: React.FC<LocationRolesViewProps> = React.memo(({
  fetchUserLocationsRoles,
  locationList,
  isSelectedAll,
  setSelectAll,
  setSelectRow,
  clearMessage,
  isSelectedAny,
  selectedNumber,
  hasError,
  isSubmitting,
  openConfirmationDialog,
  search,
  updateSearch,
  authUserId,
  currentUser,
  isGrantFullAccessEnabled,
  isGrantLimitedAccessEnabled,
  isRevokeAccessEnabled,
  assignUserLocationRoles,
  parent,
  selectedLocations,
  isSubmittingAlertSettings,
  incrementPage,
  reinitPageCount,
}) => {
  const { t } = useTranslation();
  let { userId, parentId } = useParams();
  const [selectedRole, setSelectedRole] = useState('');
  const [showAlarmSettingsModal, setShowAlarmSettingsModal] = useModalState(isSubmittingAlertSettings, hasError);

  useLocationRoleService(authUserId, userId, parentId, search, locationList.page, isSubmitting, hasError, fetchUserLocationsRoles, reinitPageCount, updateSearch);

  const handleActionClick = (role: RoleType | null) => {
    setSelectedRole(role ? role : 'revoke');
    openConfirmationDialog({ role })
  }

  const handleConfirmationAccept = (metadata: { [key: string]: any }) => {
    assignUserLocationRoles((metadata.role !== null) ? getLocationPermissions(metadata.role) : [], userId || '');
  }

  const handleUpdateSearch = (search: string) => {
    reinitPageCount();
    updateSearch(search);
  };

  return (
    <Section>
      <ToastMessage />
      <Header className={styles.header}>
        <LocationRolesHead user={currentUser} parent={parent} search={search} updateSearch={handleUpdateSearch} />
      </Header>
      <Content>
        <Col xs="12" className="pl-0 pr-0">
          <Row>
            <Col xs="12">
              <LocationRolesActions
                isSelectedAny                 = {isSelectedAny}
                selectedNumber                = {selectedNumber}
                isGrantFullAccessEnabled      = {isGrantFullAccessEnabled}
                isGrantLimitedAccessEnabled   = {isGrantLimitedAccessEnabled}
                isRevokeAccessEnabled         = {isRevokeAccessEnabled}
                onActionClick                 = {handleActionClick}
                onManageNotifications         = {() => setShowAlarmSettingsModal(true)}
              />
            </Col>
          </Row>
          <Row>
            <Col xs="12">
              <InfiniteScroll
                className       = {styles.infiniteScroll}
                dataLength      = {locationList.rows?.length || 0}
                next            = {incrementPage}
                hasMore         = {(locationList.total || 0) > (locationList.rows?.length || 0)}
                loader          = {<div className={styles.loader}><span className="spinner-border" role="status" aria-hidden="true"></span></div>}
                endMessage      = {
                  <p style={{textAlign: 'center', marginTop: 30}}>
                    <b>{t('infinite-scroll-end-message')}</b>
                  </p>
                }
              >
                <LocationRolesList
                  locationList      = {locationList}
                  isSelectedAll     = {isSelectedAll}
                  setSelectAll      = {setSelectAll}
                  setSelectRow      = {setSelectRow}
                />
              </InfiniteScroll>
              <ConfirmationDialog
                title       = {t('location-roles-confirmation-title')}
                description = {t(`location-roles-confirmation-description-${selectedRole}`, { count: selectedNumber })}
                actionText  = {t(`location-roles-confirmation-action-${selectedRole}`)}
                onAccept    = {handleConfirmationAccept}
              />
              <AlarmSettings
                show        = {showAlarmSettingsModal}
                onHide      = {() => setShowAlarmSettingsModal(false)}
                locationIds = {selectedLocations.map(loc => loc.id || '')}
                user        = {currentUser}
              />
            </Col>
          </Row>
        </Col>
      </Content>
    </Section>
  )
})
